import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Gauge, ShieldCheck, Siren, TimerReset } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";

type OrganizationRow = { id: string; name: string };
type SloRow = {
  id: string;
  metric: string;
  target_value: number;
  comparison: string;
  error_budget_pct: number | null;
  window_days: number;
  active: boolean;
  description: string | null;
};
type AlertRow = {
  id: string;
  title: string;
  message: string;
  severity: string;
  status: string;
  created_at: string;
};
type RuleRow = {
  id: string;
  name: string;
  metric: string;
  condition: string;
  threshold: number;
  severity: string;
  active: boolean;
};
type BaselineRow = { id: string };

const severityWeight: Record<string, number> = {
  critical: 4,
  high: 2,
  medium: 1,
  low: 0.5,
};

function severityVariant(severity: string): "default" | "secondary" | "destructive" | "outline" {
  if (severity === "critical" || severity === "high") return "destructive";
  if (severity === "medium") return "secondary";
  return "outline";
}

function formatWindow(date: string) {
  return new Date(date).toLocaleString("pt-BR");
}

export default function AlertsAndSlos() {
  const { data, isLoading } = useQuery({
    queryKey: ["alerts-and-slos-admin"],
    queryFn: async () => {
      const { data: orgs, error: orgErr } = await supabase
        .from("organizations")
        .select("id, name")
        .order("created_at", { ascending: true });
      if (orgErr) throw orgErr;

      const selectedOrg = ((orgs ?? []) as OrganizationRow[]).find((org) => org.name === "Syra Org") ?? (orgs ?? [])[0];
      if (!selectedOrg) {
        return { org: null, slos: [], alerts: [], rules: [], baselines: [] };
      }

      const [slosRes, alertsRes, rulesRes, baselinesRes] = await Promise.all([
        supabase
          .from("jarvis_slos")
          .select("id, metric, target_value, comparison, error_budget_pct, window_days, active, description")
          .eq("org_id", selectedOrg.id)
          .order("metric"),
        supabase
          .from("sentinel_alerts")
          .select("id, title, message, severity, status, created_at")
          .eq("org_id", selectedOrg.id)
          .order("created_at", { ascending: false })
          .limit(20),
        supabase
          .from("sentinel_rules")
          .select("id, name, metric, condition, threshold, severity, active")
          .eq("org_id", selectedOrg.id)
          .order("metric"),
        supabase
          .from("sentinel_baselines")
          .select("id")
          .eq("org_id", selectedOrg.id)
          .is("valid_until", null),
      ]);

      if (slosRes.error) throw slosRes.error;
      if (alertsRes.error) throw alertsRes.error;
      if (rulesRes.error) throw rulesRes.error;
      if (baselinesRes.error) throw baselinesRes.error;

      return {
        org: selectedOrg as OrganizationRow,
        slos: (slosRes.data ?? []) as SloRow[],
        alerts: (alertsRes.data ?? []) as AlertRow[],
        rules: (rulesRes.data ?? []) as RuleRow[],
        baselines: (baselinesRes.data ?? []) as BaselineRow[],
      };
    },
    refetchInterval: 30_000,
  });

  const metrics = useMemo(() => {
    const slos = data?.slos ?? [];
    const alerts = data?.alerts ?? [];
    const openAlerts = alerts.filter((alert) => ["open", "acknowledged"].includes(alert.status));
    const oneHourAgo = Date.now() - 3600_000;
    const sixHoursAgo = Date.now() - 6 * 3600_000;
    const burn = (from: number) => openAlerts
      .filter((alert) => new Date(alert.created_at).getTime() >= from)
      .reduce((sum, alert) => sum + (severityWeight[alert.severity] ?? 1), 0) / Math.max(slos.filter((slo) => slo.active).length, 1);

    return {
      activeSlos: slos.filter((slo) => slo.active).length,
      openAlerts: openAlerts.length,
      burnRate1h: burn(oneHourAgo),
      burnRate6h: burn(sixHoursAgo),
      baselineCoverage: data?.baselines.length ?? 0,
    };
  }, [data]);

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Admin", to: "/admin" }, { label: "Alerts & SLOs" }]} />
      <ShadowModeBanner context="Alguns alertas dependem de métricas de Fase 3 ainda em shadow." compact />

      <div>
        <h1 className="text-2xl font-bold font-headline">Alerts & SLOs</h1>
        <p className="text-sm text-muted-foreground">
          Superfície canônica de governança operacional para {data?.org?.name ?? "a organização ativa"}.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard title="SLOs ativos" value={isLoading ? "—" : String(metrics.activeSlos)} icon={ShieldCheck} hint="Catálogo canônico" />
        <MetricCard title="Alertas abertos" value={isLoading ? "—" : String(metrics.openAlerts)} icon={Siren} hint="Open + acknowledged" />
        <MetricCard title="Burn rate 1h" value={isLoading ? "—" : metrics.burnRate1h.toFixed(2)} icon={Gauge} hint="Peso/ SLO ativo" />
        <MetricCard title="Burn rate 6h" value={isLoading ? "—" : metrics.burnRate6h.toFixed(2)} icon={TimerReset} hint="Pressão sustentada" />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Alertas recentes</CardTitle>
            <CardDescription>Fila operacional priorizada por severidade.</CardDescription>
          </CardHeader>
          <CardContent>
            {!data?.alerts.length ? (
              <p className="text-sm text-muted-foreground">Sem alertas recentes.</p>
            ) : (
              <div className="space-y-3">
                {data.alerts.map((alert) => (
                  <div key={alert.id} className="rounded-md border p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium leading-tight">{alert.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{alert.message}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Badge variant={severityVariant(alert.severity)}>{alert.severity}</Badge>
                        <Badge variant="outline">{alert.status}</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{formatWindow(alert.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cobertura operacional</CardTitle>
            <CardDescription>Regras, baselines e pressão de erro em uma visão só.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <CoverageRow label="Regras ativas" value={`${data?.rules.filter((rule) => rule.active).length ?? 0}`} />
            <CoverageRow label="Baselines vigentes" value={`${metrics.baselineCoverage}`} />
            <CoverageRow label="Meta burn 1h" value={metrics.burnRate1h > 1 ? "Acima do ideal" : "Dentro do ideal"} />
            <CoverageRow label="Meta burn 6h" value={metrics.burnRate6h > 1 ? "Pressão sustentada" : "Estável"} />
            <CoverageRow label="Risco imediato" value={metrics.openAlerts > 0 ? "Exige triagem" : "Sem fila crítica"} icon={AlertTriangle} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Catálogo de SLOs</CardTitle>
          <CardDescription>Targets ativos usados como contrato operacional desta fase.</CardDescription>
        </CardHeader>
        <CardContent>
          {!data?.slos.length ? (
            <p className="text-sm text-muted-foreground">Nenhum SLO semeado ainda.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Métrica</TableHead>
                  <TableHead>Alvo</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Janela</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.slos.map((slo) => (
                  <TableRow key={slo.id}>
                    <TableCell>
                      <div className="font-medium">{slo.metric}</div>
                      <div className="text-xs text-muted-foreground">{slo.description ?? "—"}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{slo.comparison} {Number(slo.target_value).toFixed(2)}</TableCell>
                    <TableCell className="text-xs">{slo.error_budget_pct != null ? `${Number(slo.error_budget_pct).toFixed(2)}%` : "—"}</TableCell>
                    <TableCell className="text-xs">{slo.window_days}d</TableCell>
                    <TableCell><Badge variant={slo.active ? "default" : "outline"}>{slo.active ? "ativo" : "inativo"}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Regras do evaluator</CardTitle>
          <CardDescription>Regras atualmente executáveis pelo Sentinel evaluator.</CardDescription>
        </CardHeader>
        <CardContent>
          {!data?.rules.length ? (
            <p className="text-sm text-muted-foreground">Nenhuma regra ativa.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {data.rules.map((rule) => (
                <div key={rule.id} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium leading-tight">{rule.name}</p>
                    <Badge variant={severityVariant(rule.severity)}>{rule.severity}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{rule.metric} {rule.condition} {Number(rule.threshold).toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ title, value, hint, icon: Icon }: { title: string; value: string; hint: string; icon: typeof ShieldCheck }) {
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      </CardContent>
    </Card>
  );
}

function CoverageRow({ label, value, icon: Icon }: { label: string; value: string; icon?: typeof AlertTriangle }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        {Icon ? <Icon className="h-4 w-4 text-primary" /> : null}
        <span className="text-muted-foreground">{label}</span>
      </div>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}